package gr.tech.GRAttestation.components.inputs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.GRAttestation.styles.*
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.events.SyntheticChangeEvent
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement

enum class ValidationTrigger {
    OnBlur,
    OnChange
}

@Composable
fun StringInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    subtitleText: String = "",
    value: String,
    hasError: Boolean = false,
    showError: Boolean = true,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 128,
    isRequired: Boolean = false,
    title: String = "",
    placeholder: String = "",
    password: Boolean = false,
    validationTrigger: ValidationTrigger = ValidationTrigger.OnBlur,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        if (labelText.isNotBlank()) {
            Label(
                forId = inputId,
                attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
            ) { Text(labelText) }
        }
        Input(
            if (password) InputType.Password else InputType.Text,
            attrs = Modifier.then(inputModifier)
                .id(inputId)
                .thenIf(hasError, Modifier.Companion.bsInputErrorOutlineStyle())
                .toAttrs {
                    if (isRequired) required()
                    minLength(minLength)
                    maxLength(maxLength)
                    attr("value", value)
                    title(title)
                    placeholder(placeholder)
                    if (validationTrigger == ValidationTrigger.OnChange) {
                        onInput { event -> onValueChange(event.target.value) }
                    } else {
                        onChange { event -> onValueChange(event.target.value) }
                    }
                }
        )
        if (subtitleText.isNotBlank()) {
            SpanText(
                modifier = Modifier.classNames("form-text"),
                text = subtitleText
            )
        }
        if (showError) {
            P(Modifier.Companion.bsInputErrorTextStyle().toAttrs()) {
                Text(labelError)
            }
        }
    }
}

@Composable
fun IntegerInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: Int?,
    min: Int = 0,
    max: Int = Int.MAX_VALUE,
    hasError: Boolean = false,
    showError: Boolean = true,
    labelError: String = " ",
    isRequired: Boolean = false,
    placeholder: String = "",
    onValueChange: (Int?) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        if (labelText.isNotBlank()) {
            Label(
                forId = inputId,
                attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
            ) { Text(labelText) }
        }
        Input(
            InputType.Number,
            attrs = Modifier.then(inputModifier)
                .id(inputId)
                .thenIf(hasError, Modifier.Companion.bsInputErrorOutlineStyle())
                .toAttrs {
                    if (isRequired) required()
                    min(min.toString())
                    max(max.toString())
                    value(value?.toString() ?: "")
                    placeholder(placeholder)
                    onInput { event -> onValueChange(event.target.value.toIntOrNull()) }
                }
        )
        if (showError) {
            P(Modifier.Companion.bsInputErrorTextStyle().toAttrs()) {
                Text(labelError)
            }
        }
    }
}

@Composable
fun StringTextArea(
    direction: FlexDirection = FlexDirection.Column,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    textAreaModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    hasError: Boolean = false,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 512,
    isRequired: Boolean = false,
    title: String = "",
    rows: Int = 4,
    validationTrigger: ValidationTrigger = ValidationTrigger.OnBlur,
    onValueChange: (String) -> Unit,
) {

    LaunchedEffect(value) {
        try {
            val input = (document.getElementById(inputId) as HTMLTextAreaElement)
            input.value = value
        } catch (e: Exception) {
            console.error("Error inputting Bio!")
        }
    }

    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) { Text(labelText) }
        TextArea(
            attrs = Modifier.then(textAreaModifier)
                .id(inputId)
                .thenIf(hasError, Modifier.Companion.bsInputErrorOutlineStyle())
                .toAttrs {
                    if (isRequired) required()
                    minLength(minLength)
                    maxLength(maxLength)
                    rows(rows)
                    title(title)
                    attr("value", value) // for controlled textarea
                    if (validationTrigger == ValidationTrigger.OnChange) {
                        onInput { event -> onValueChange(event.target.value) }
                    } else {
                        onChange { event -> onValueChange(event.target.value) }
                    }
                }
        )
        P(Modifier.Companion.bsInputErrorTextStyle().toAttrs()) {
            Text(labelError)
        }
    }
}

@Composable
fun EmailInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    inputModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    subtitleText: String = "",
    value: String,
    hasError: Boolean = false,
    showError: Boolean = true,
    labelError: String = " ",
    minLength: Int = 3,
    maxLength: Int = 128,
    isRequired: Boolean = false,
    title: String = "",
    placeholder: String = "",
    validationTrigger: ValidationTrigger = ValidationTrigger.OnBlur,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        if (labelText.isNotBlank()) {
            Label(
                forId = inputId,
                attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
            ) { Text(labelText) }
        }
        Input(
            InputType.Email,
            attrs = Modifier.then(inputModifier)
                .id(inputId)
                .thenIf(hasError, Modifier.Companion.bsInputErrorOutlineStyle())
                .toAttrs {
                    if (isRequired) required()
                    minLength(minLength)
                    maxLength(maxLength)
                    attr("value", value)
                    title(title)
                    placeholder(placeholder)
                    if (validationTrigger == ValidationTrigger.OnChange) {
                        onInput { event -> onValueChange(event.target.value) }
                    } else {
                        onChange { event -> onValueChange(event.target.value) }
                    }
                }
        )
        if (subtitleText.isNotBlank()) {
            SpanText(
                modifier = Modifier.classNames("form-text"),
                text = subtitleText
            )
        }
        if (showError) {
            P(Modifier.Companion.bsInputErrorTextStyle().toAttrs()) {
                Text(labelError)
            }
        }
    }
}

@Composable
fun SelectInput(
    direction: FlexDirection = FlexDirection.Row,
    justifyContent: JustifyContent = JustifyContent.Start,
    alignItems: AlignItems = AlignItems.Start,
    containerModifier: Modifier = Modifier,
    labelModifier: Modifier = Modifier,
    selectModifier: Modifier = Modifier,
    inputId: String = "",
    labelText: String = "",
    value: String,
    options: List<Pair<String, String>>, // Pair<Index String, Label String>
    hasError: Boolean = false,
    labelError: String = " ",
    isRequired: Boolean = false,
    showError: Boolean = false,
    title: String = "",
    onInputChange: (SyntheticChangeEvent<String?, HTMLSelectElement>) -> Unit,
) {
    Div(
        attrs = InputContainerStyle.toModifier()
            .flexDirection(direction)
            .justifyContent(justifyContent)
            .alignItems(alignItems)
            .then(containerModifier)
            .toAttrs()
    ) {
        Label(
            forId = inputId,
            attrs = GeneralInputLabelStyle.toModifier().then(labelModifier).toAttrs()
        ) {
            Text(labelText)
        }

        Select(
            attrs = Modifier
                .id(inputId)
                .thenIf(hasError, GeneralInputErrorStyle.toModifier())
                .then(selectModifier)
                .toAttrs {
                    if (isRequired) required()
                    title(title)
                    attr("value", value)
                    onChange { onInputChange(it) }
                }
        ) {
            // Placeholder option
            Option(
                value = "",
                attrs = Modifier.toAttrs {
                    attr("disabled", "")
                    attr("hidden", "")
                    if (value.isEmpty()) attr("selected", "")
                }
            ) {
                Text("Select Option")
            }

            // Actual options
            options.forEach { (optionValue, optionLabel) ->
                Option(
                    value = optionValue,
                    attrs = Modifier.toAttrs {
                        if (optionValue == value) attr("selected", "")
                    }
                ) {
                    Text(optionLabel)
                }
            }
        }

        if (showError) {
            P(GeneralFormErrorTextStyle.toModifier().toAttrs()) {
                Text(labelError)
            }
        }
    }
}
